/**
 * Taskbar - Barra de tareas profesional para ventanas
 * Estilo Bloomberg/Trading Terminal con controles avanzados
 */
import React from 'react';
import { CaretUp, X, ArrowsInSimple, ArrowsOutSimple, XCircle, Bell } from '@phosphor-icons/react';

const Taskbar = ({ 
  minimizedWindows = [], 
  openWindows = [],
  onRestore, 
  onClose,
  onMinimizeAll,
  onRestoreAll,
  onCloseAll,
  openWindowsCount = 0,
  alertsMinimized = false,
  onRestoreAlerts,
  onCloseAlerts
}) => {
  // No mostrar si no hay ventanas ni alertas minimizadas
  if (openWindowsCount === 0 && minimizedWindows.length === 0 && !alertsMinimized) return null;

  const activeWindowsCount = openWindowsCount - minimizedWindows.length;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-[200] pointer-events-none"
      style={{ marginLeft: '64px' }}
      data-testid="taskbar"
    >
      {/* Barra de tareas - MÁS COMPACTA */}
      <div 
        className="mx-4 mb-1 pointer-events-auto"
        style={{
          background: 'linear-gradient(180deg, rgba(10, 22, 40, 0.92) 0%, rgba(5, 15, 30, 0.95) 100%)',
          borderRadius: '10px',
          border: '1px solid rgba(0, 255, 255, 0.2)',
          boxShadow: '0 -2px 20px rgba(0, 0, 0, 0.3), 0 0 15px rgba(0, 255, 255, 0.08)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <div className="flex items-center justify-between px-3 py-1.5">
          {/* Lado izquierdo - Estadísticas y controles */}
          <div className="flex items-center gap-2">
            {/* Contador de ventanas - más compacto */}
            <div 
              className="flex items-center gap-1.5 px-2 py-1 rounded-md"
              style={{
                background: 'linear-gradient(135deg, rgba(0, 255, 255, 0.08) 0%, rgba(0, 255, 106, 0.03) 100%)',
                border: '1px solid rgba(0, 255, 255, 0.25)',
              }}
            >
              <div className="flex items-center gap-1">
                <span 
                  className="text-sm font-bold"
                  style={{ color: '#00dcff', textShadow: '0 0 8px rgba(0, 220, 255, 0.4)' }}
                >
                  {activeWindowsCount}
                </span>
                <span className="text-[10px] text-gray-500">activas</span>
              </div>
              {minimizedWindows.length > 0 && (
                <>
                  <div className="w-px h-3 bg-gray-700" />
                  <div className="flex items-center gap-1">
                    <span 
                      className="text-sm font-bold"
                      style={{ color: '#ffd700', textShadow: '0 0 8px rgba(255, 215, 0, 0.4)' }}
                    >
                      {minimizedWindows.length}
                    </span>
                    <span className="text-[10px] text-gray-500">min</span>
                  </div>
                </>
              )}
            </div>
            
            {/* Botones de control - más pequeños */}
            <div className="flex items-center gap-0.5">
              {/* Minimizar todas */}
              {activeWindowsCount > 0 && (
                <button
                  onClick={onMinimizeAll}
                  className="flex items-center gap-1 px-2 py-1 rounded-md transition-all hover:scale-105"
                  style={{
                    background: 'rgba(255, 215, 0, 0.08)',
                    border: '1px solid rgba(255, 215, 0, 0.25)',
                  }}
                  title="Minimizar todas"
                  data-testid="taskbar-minimize-all"
                >
                  <ArrowsInSimple size={12} className="text-yellow-400" />
                  <span className="text-[9px] font-medium text-yellow-400 hidden sm:inline">Min</span>
                </button>
              )}
              
              {/* Restaurar todas */}
              {minimizedWindows.length > 0 && (
                <button
                  onClick={onRestoreAll}
                  className="flex items-center gap-1 px-2 py-1 rounded-md transition-all hover:scale-105"
                  style={{
                    background: 'rgba(0, 255, 106, 0.08)',
                    border: '1px solid rgba(0, 255, 106, 0.25)',
                  }}
                  title="Restaurar todas"
                  data-testid="taskbar-restore-all"
                >
                  <ArrowsOutSimple size={12} className="text-green-400" />
                </button>
              )}
              
              {/* Cerrar todas */}
              {openWindowsCount > 0 && (
                <button
                  onClick={onCloseAll}
                  className="flex items-center gap-1 px-2 py-1 rounded-md transition-all hover:scale-105"
                  style={{
                    background: 'rgba(239, 68, 68, 0.08)',
                    border: '1px solid rgba(239, 68, 68, 0.25)',
                  }}
                  title="Cerrar todas"
                  data-testid="taskbar-close-all"
                >
                  <XCircle size={12} className="text-red-400" />
                </button>
              )}
            </div>
          </div>

          {/* Lado derecho - Ventanas minimizadas - más compactas */}
          <div className="flex items-center gap-1.5 flex-wrap justify-end max-w-[65%] overflow-x-auto">
            {/* Centro de Alertas minimizado */}
            {alertsMinimized && (
              <div
                className="group flex items-center gap-1.5 px-2 py-1 rounded-md cursor-pointer transition-all hover:scale-105 shrink-0"
                style={{
                  background: 'linear-gradient(135deg, #f59e0b20 0%, #f59e0b08 100%)',
                  border: '1px solid #f59e0b50',
                  boxShadow: '0 1px 8px #f59e0b20'
                }}
                onClick={onRestoreAlerts}
                data-testid="taskbar-item-alerts"
              >
                {/* Icono */}
                <div 
                  className="w-5 h-5 rounded flex items-center justify-center"
                  style={{ background: '#f59e0b25' }}
                >
                  <Bell size={12} weight="fill" style={{ color: '#f59e0b' }} />
                </div>
                
                {/* Nombre */}
                <span className="text-[10px] font-semibold max-w-[60px] truncate" style={{ color: '#f59e0b' }}>
                  Alertas
                </span>
                
                {/* Botón cerrar en hover */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onCloseAlerts();
                  }}
                  className="p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-red-500/30 transition-all"
                  title="Cerrar"
                >
                  <X size={10} className="text-gray-400 hover:text-red-400" />
                </button>
              </div>
            )}
            
            {minimizedWindows.map((window) => {
              const Icon = window.tool.icon;
              return (
                <div
                  key={window.windowId}
                  className="group flex items-center gap-1.5 px-2 py-1 rounded-md cursor-pointer transition-all hover:scale-105 shrink-0"
                  style={{
                    background: `linear-gradient(135deg, ${window.tool.color}20 0%, ${window.tool.color}08 100%)`,
                    border: `1px solid ${window.tool.color}50`,
                    boxShadow: `0 1px 8px ${window.tool.color}20`
                  }}
                  onClick={() => onRestore(window.windowId)}
                  data-testid={`taskbar-item-${window.toolId}`}
                >
                  {/* Punto de acento de la ventana (color elegido por el usuario) */}
                  <span
                    className="w-2.5 h-2.5 rounded-full shrink-0 border border-white/30"
                    style={{
                      background: window.accentColor || window.tool.color,
                      boxShadow: `0 0 6px ${window.accentColor || window.tool.color}`
                    }}
                    title="Acento de la ventana"
                    data-testid={`taskbar-accent-dot-${window.toolId}`}
                  />
                  {/* Icono */}
                  <div 
                    className="w-5 h-5 rounded flex items-center justify-center"
                    style={{
                      background: `${window.tool.color}25`,
                    }}
                  >
                    <Icon 
                      size={12} 
                      weight="fill" 
                      style={{ color: window.tool.color }} 
                    />
                  </div>
                  
                  {/* Nombre */}
                  <span 
                    className="text-[10px] font-semibold max-w-[60px] truncate"
                    style={{ color: window.tool.color }}
                  >
                    {window.tool.name}
                  </span>
                  
                  {/* Botón cerrar en hover */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onClose(window.windowId);
                    }}
                    className="p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-red-500/30 transition-all"
                    title="Cerrar"
                  >
                    <X size={10} className="text-gray-400 hover:text-red-400" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Taskbar;
